import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquareHeart, Reply } from "lucide-react";
import { cn } from "@/lib/utils";
import usePermissions from "@/hooks/usePermissions";
import { useOrgFeedSettings, useUpdateOrgFeedSettings } from "@/hooks/useOrgFeedSettings";
import {
  useOrgReplyDisplayName,
  useUpdateOrgReplyDisplayName,
  REPLY_DISPLAY_NAME_MAX_LENGTH,
} from "@/hooks/useOrgReplyDisplayName";
import { OrgAccessSettingsCard } from "@/components/corporate/organizations/access/OrgAccessSettingsCard";
import { SectionFrame } from "@/components/corporate/organizations/page/SectionFrame";
import { useReportDirty } from "@/components/corporate/organizations/page/useSectionDraft";
import type { Organization } from "@/types/organizations";

interface FeaturesSectionProps {
  organization: Organization;
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Settings → Features & access: the Social Feed master switch, Sign-in & access (guests, perks,
 * health onboarding, chat — chat admin-only) and the reply display name. Every switch saves at
 * once, as before; the reply name has its own Save. Only live settings — no fake rows, no Save All.
 */
export function FeaturesSection({ organization, onDirtyChange }: FeaturesSectionProps) {
  const { isAthlynAdmin } = usePermissions();
  return (
    <SectionFrame
      section="features"
      title="Features & access"
      hint="Only the switches that really work. Each switch saves at once; the reply name has its own Save."
    >
      <SocialFeedCard organizationId={organization.id} />
      <div className="border-t border-ds-line pt-4">
        <OrgAccessSettingsCard organizationId={organization.id} canChangeChat={isAthlynAdmin} />
      </div>
      <ReplyDisplayNameCard organizationId={organization.id} onDirtyChange={onDirtyChange} />
    </SectionFrame>
  );
}

/** The Social Feed master switch — sends `masterFeedEnabled` only, so HR's own feed settings are never resent (B4). */
function SocialFeedCard({ organizationId }: { organizationId: string }) {
  const { data: feedSettings, isLoading } = useOrgFeedSettings(organizationId);
  const updateFeedSettings = useUpdateOrgFeedSettings(organizationId);

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-4" data-testid="social-feed-feature-row">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <MessageSquareHeart className="h-5 w-5" />
        </div>
        <div>
          <h4 className="font-medium">Social Feed</h4>
          <p className="text-sm text-muted-foreground">
            Master switch for the employee social feed, stickers and kudos. When off, the feed is hidden for all
            employees in this organization.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isLoading ? (
          <Skeleton className="h-6 w-11 rounded-full" />
        ) : (
          <Switch
            checked={!!feedSettings?.masterFeedEnabled}
            onCheckedChange={(enabled) => updateFeedSettings.mutate({ masterFeedEnabled: enabled })}
            disabled={updateFeedSettings.isPending}
            aria-label="Toggle Social Feed"
          />
        )}
      </div>
    </div>
  );
}

/** The name feedback replies are signed with; blank clears to the registered company name. */
function ReplyDisplayNameCard({
  organizationId,
  onDirtyChange,
}: {
  organizationId: string;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { data: replyName, isLoading } = useOrgReplyDisplayName(organizationId);
  const updateReplyName = useUpdateOrgReplyDisplayName(organizationId);

  const [replyNameInput, setReplyNameInput] = useState("");
  // Sync the local editor with the server value once it loads / changes.
  useEffect(() => {
    setReplyNameInput(replyName?.replyDisplayName ?? "");
  }, [replyName?.replyDisplayName]);

  const trimmed = replyNameInput.trim();
  const tooLong = trimmed.length > REPLY_DISPLAY_NAME_MAX_LENGTH;
  const dirty = !isLoading && trimmed !== (replyName?.replyDisplayName ?? "");
  useReportDirty(onDirtyChange, dirty);

  const onSave = () => {
    if (tooLong) return;
    updateReplyName.mutate({ replyDisplayName: replyNameInput });
  };

  return (
    <section data-testid="reply-display-name-card" data-flush="true" className="space-y-3 border-t border-ds-line pt-4">
      <div>
        <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
          <Reply className="h-4 w-4" aria-hidden="true" />
          Reply display name
        </h3>
        <p className="mt-0.5 text-[13px] text-ds-ink2">
          The name this organization&apos;s replies to employee feedback are signed with.
        </p>
      </div>
      <div className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="reply-display-name">Display name</Label>
              <Input
                id="reply-display-name"
                value={replyNameInput}
                placeholder="e.g. People Team"
                onChange={(e) => setReplyNameInput(e.target.value)}
                aria-label="Reply display name"
                aria-invalid={tooLong}
                disabled={updateReplyName.isPending}
              />
              <p className={cn("text-sm", tooLong ? "text-destructive" : "text-muted-foreground")}>
                {tooLong
                  ? `Keep it to ${REPLY_DISPLAY_NAME_MAX_LENGTH} characters or fewer.`
                  : "Shown on this organization's replies to employee feedback — leave empty to use its registered company name."}
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={onSave} disabled={updateReplyName.isPending || tooLong || !dirty}>
                {updateReplyName.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default FeaturesSection;
